import {Organization} from '@mintdata-wsp/common/state/DataState';
import {IDesignStateSnapshot} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignState';
import {computed, observable} from 'mobx';

export type ProjectInfo = {
    id:number,
    name:string,
    designs:Array<DesignInfo>
};

export type DesignInfo = {
    id:number,
    projectId?:number,
    name:string,
    projectName?:string
};

export type VersionInfo = {
    id:number,
    name:string,
    description?:string
};

export type PageInfo = {
    id:string,
    name:string,
    selected:boolean,
    dependencies:Array<string>;
};

export type SheetInfo = {
    id:string,
    name:string,
    selected:boolean,
};

export enum ListTypes
{
    Pages = 'Pages',
    Sheets = 'Sheets',
}

export enum ResetStateType
{
    All = 100,
    OrganizationChange = 80,
    SpaceDesignChange = 50,
    VersionChange = 20,
    PagesAndSheetsLoad = 0,
}

export class CopyPagesDesignState
{
    @observable
    public isCopyRelatedSheets:boolean = true;

    @observable
    public copyNamePrefix:string = '';

    @observable
    public selectedProjectId:number = null;

    @observable
    public selectedDesignId:number = null;

    @observable
    public selectedVersionId:number = null;

    @observable
    public selectedDesignSnapshot:IDesignStateSnapshot = null;

    @observable
    public pages:Array<PageInfo> = [];

    @observable
    public sheets:Array<SheetInfo> = [];
}

export class CopyPagesState
{
    public readonly designState:CopyPagesDesignState = new CopyPagesDesignState();

    @observable
    public isCopyNamePrefixDirty:boolean = false;

    @observable
    public organizations:Array<Organization> = [];

    @observable
    public organizationsLoading:boolean = false;

    @observable
    public selectedOrganizationSid:string = null;

    // it needs for saving title in dropdown while we reset all organizations
    @observable
    public selectedOrganizationName:string = null;

    @observable
    public versions:Array<VersionInfo> = [];

    @observable
    public designsSearchString:string = null;

    @observable
    public projects:Array<ProjectInfo> = [];

    @observable
    public designsLoading:boolean = false;

    public designsLoadingPromise:Promise<any> = null;

    // it needs for saving title in dropdown while we reset all projects
    @observable
    public selectedProjectAndDesignName:string = null;

    @observable
    public versionsLoading:boolean = false;

    public versionsLoadingPromise:Promise<any> = null;

    @observable
    public pagesAndSheetsLoading:boolean = false;

    @observable
    public currentListType:ListTypes = ListTypes.Pages;

    @observable
    public isCopyingInProgress:boolean = false;

    @observable
    public shouldShowLoader:boolean = false;

    @computed
    public get selectedDesign():DesignInfo
    {
        const designId = this.designState.selectedDesignId;
        let design:DesignInfo = null;

        for( let i:number = 0; i < this.projects.length; i++ )
        {
            design = this.projects[i].designs.find(design => design.id === designId);

            if( design )
                break;
        }

        return design;
    }

    @computed
    public get projectsFiltered():Array<ProjectInfo>
    {
        if( !this.designsSearchString )
            return this.projects;

        let result:Array<ProjectInfo> = [];
        const filterStr:string = this.designsSearchString.toLowerCase();

        for( let i:number = 0; i < this.projects.length; i++ )
        {
            if( this.projects[i].name.toLowerCase().includes(filterStr) )
            {
                result.push(this.projects[i]);
                continue;
            }

            const designs:Array<DesignInfo> = this.projects[i].designs.filter(
                (design:DesignInfo) => design.name.toLowerCase().includes(filterStr)
            );

            if( designs.length > 0 )
            {
                result.push({
                    ...this.projects[i],
                    designs,
                });
            }
        }

        return result;
    }

    public reset(type:ResetStateType = ResetStateType.PagesAndSheetsLoad)
    {
        if( type >= ResetStateType.All )
        {
            this.designState.isCopyRelatedSheets = true;
            this.designState.copyNamePrefix = '';

            this.isCopyNamePrefixDirty = false;
            this.isCopyingInProgress = false;

            this.organizations = [];
            this.selectedOrganizationSid = null;
            this.selectedOrganizationName = null;

            this.currentListType = ListTypes.Pages;
        }

        if( type >= ResetStateType.OrganizationChange )
        {
            this.organizationsLoading = false;
            this.projects = [];
            this.designState.selectedDesignId = null;
            this.selectedProjectAndDesignName = null;
        }

        if( type >= ResetStateType.SpaceDesignChange )
        {
            this.designsLoading = false;
            this.designsSearchString = null;
            this.designState.selectedDesignSnapshot = null;

            this.versions = [];
            this.designState.selectedVersionId = null;
        }

        if( type >= ResetStateType.VersionChange )
        {
            this.versionsLoading = false;
        }

        this.pagesAndSheetsLoading = false;
        this.designState.pages = [];
        this.designState.sheets = [];
    }

    public resetSelectedItems():void
    {
        this.designState.pages.forEach((page:PageInfo) =>
        {
            page.selected = false;
        });
        this.designState.sheets.forEach((sheet:SheetInfo) =>
        {
            sheet.selected = false;
        });
    }
}
